import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Tag, 
  Space, 
  Row, 
  Col, 
  Statistic, 
  Badge, 
  message, 
  Spin,
  Tooltip,
  Segmented,
  Typography,
  Empty
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import Layout from '../../layout/Layout';
import fetchDataUtil from '../../functions/fetchData';
import { getHeaders } from '../../utility/getHeader';
import axios from 'axios';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const EmployeePositions = () => {
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [form] = Form.useForm();
  const [formData, setFormData] = useState({
    position_name: '',
    position_code: '',
    department_id: '',
    description: '',
    status: 'active'
  });

  useEffect(() => {
    fetchData();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      // Load departments from database using fetchData utility
      const departmentsData = await fetchDataUtil('employee_departments', null, 'id', {});
      setDepartments(departmentsData || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
      message.error('Failed to load departments');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      // Using fetchData function directly with correct format for positions only
      const positionsData = await fetchDataUtil('employee_positions', null, 'id', {});
      setPositions(positionsData || []);
    } catch (error) {
      message.error('Failed to fetch data');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingPosition) {
        // Update position
        await axios.put(`/updatecommondata/employee_positions/id/${editingPosition.id}`, {
          position_name: values.position_name,
          position_code: values.position_code,
          department_id: parseInt(values.department_id),
          description: values.description || '',
          is_active: values.status === 'active'
        }, getHeaders());
        message.success('Position updated successfully!');
      } else {
        // Create position
        await axios.post('/insertdata/employee_positions', {
          position_name: values.position_name,
          position_code: values.position_code,
          department_id: parseInt(values.department_id),
          description: values.description || '',
          is_active: values.status === 'active'
        }, getHeaders());
        message.success('Position created successfully!');
      }
      
      setShowModal(false);
      setEditingPosition(null);
      form.resetFields();
      fetchData();
    } catch (error) {
      if (error.errorFields) {
        // Validation error
        return;
      }
      
      console.error('Error saving position:', error);
      let errorMessage = 'Failed to save position';
      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid position data. Please check your inputs.';
            break;
          case 401:
            errorMessage = 'Session expired. Please login again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 409:
            errorMessage = 'Position name already exists in this department.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = error.response.data?.message || 'Failed to save position';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      message.error(errorMessage);
    }
  };

  const handleEdit = (position) => {
    setEditingPosition(position);
    form.setFieldsValue({
      position_name: position.position_name,
      position_code: position.position_code || '',
      department_id: position.department_id?.toString() || '',
      description: position.description || '',
      status: position.is_active ? 'active' : 'inactive'
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Position',
      content: 'Are you sure you want to delete this position?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          await axios.delete(`/deletebyid/employee_positions/id/${id}`, getHeaders());
          message.success('Position deleted successfully!');
          fetchData();
        } catch (error) {
          console.error('Error deleting position:', error);
          
          let errorMessage = 'Failed to delete position';
          if (error.response) {
            switch (error.response.status) {
              case 400:
                errorMessage = 'Cannot delete position. It may be assigned to employees.';
                break;
              case 401:
                errorMessage = 'Session expired. Please login again.';
                break;
              case 403:
                errorMessage = 'You do not have permission to delete positions.';
                break;
              case 404:
                errorMessage = 'Position not found.';
                break;
              case 500:
                errorMessage = 'Server error. Please try again later.';
                break;
              default:
                errorMessage = error.response.data?.message || 'Failed to delete position';
            }
          } else if (error.request) {
            errorMessage = 'Network error. Please check your connection.';
          }
          
          message.error(errorMessage);
        }
      }
    });
  };

  const getDepartmentName = (departmentId) => {
    // For combolist API, departments might have different structure
    // Check if it's the standard combolist format with value/label or id/department_name
    const department = departments.find(d => 
      (d.value && d.value.toString() === departmentId?.toString()) ||
      (d.id && d.id.toString() === departmentId?.toString())
    );
    if (department) {
      return department.label || department.department_name || 'Unknown Department';
    }
    return 'Unknown Department';
  };

  const filteredPositions = positions.filter(position => {
    const matchesSearch = position.position_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (position.description && position.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDepartment = !selectedDepartment || position.department_id?.toString() === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  // Auto-generate position code from position name
  const generatePositionCode = (positionName) => {
    if (!positionName || positionName.trim() === '') return '';
    
    // Extract first letters of each word and convert to uppercase
    const words = positionName.trim().split(/\s+/);
    let code = '';
    
    if (words.length === 1) {
      // Single word: take first 3-4 letters
      code = words[0].substring(0, 4).toUpperCase();
    } else {
      // Multiple words: take first letter of each word
      code = words.map(word => word.charAt(0).toUpperCase()).join('');
    }
    
    // Add random 2-digit number for uniqueness
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return code + randomNum;
  };

  // Handle position name change to auto-generate code
  const handlePositionNameChange = (e) => {
    const newName = e.target.value;
    form.setFieldsValue({ position_name: newName });
    
    // Only auto-generate for new positions, not when editing
    if (!editingPosition) {
      const generatedCode = generatePositionCode(newName);
      form.setFieldsValue({ position_code: generatedCode });
    }
  };

  // Table columns definition
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: 'Position Name',
      dataIndex: 'position_name',
      key: 'position_name',
      sorter: (a, b) => a.position_name.localeCompare(b.position_name),
    },
    {
      title: 'Code',
      dataIndex: 'position_code',
      key: 'position_code',
      width: 120,
      render: (code) => <Tag color="blue">{code || 'N/A'}</Tag>,
    },
    {
      title: 'Department',
      dataIndex: 'department_id',
      key: 'department_id',
      render: (departmentId) => getDepartmentName(departmentId),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => text || 'No description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      align: 'center',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div style={{ padding: '24px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <TeamOutlined /> Position Management
          </Title>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Total Positions"
                value={loading ? '...' : positions.length}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Active Positions"
                value={loading ? '...' : positions.filter(p => p.status === 'active' || p.is_active).length}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Departments"
                value={loading ? '...' : departments.length}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content Card */}
        <Card>
          {/* Toolbar */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search positions..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="All Departments"
                style={{ width: '100%' }}
                value={selectedDepartment}
                onChange={(value) => setSelectedDepartment(value)}
                allowClear
              >
                {departments.map(dept => (
                  <Option key={dept.value || dept.id} value={(dept.value || dept.id)?.toString()}>
                    {dept.label || dept.department_name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={24} md={8} style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingPosition(null);
                  form.resetFields();
                  form.setFieldsValue({ status: 'active' });
                  setShowModal(true);
                }}
              >
                Add Position
              </Button>
            </Col>
          </Row>

          {/* Table View */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" tip="Loading positions..." />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredPositions}
              rowKey="id"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} positions`,
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No positions found"
                  />
                ),
              }}
            />
          )}
        </Card>

        {/* Add/Edit Modal */}
        <Modal
          title={
            <Space>
              <TeamOutlined />
              {editingPosition ? 'Edit Position' : 'Add New Position'}
            </Space>
          }
          open={showModal}
          onOk={handleSubmit}
          onCancel={() => {
            setShowModal(false);
            setEditingPosition(null);
            form.resetFields();
          }}
          okText={editingPosition ? 'Update' : 'Create'}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            initialValues={{ status: 'active' }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="position_name"
                  label="Position Name"
                  rules={[
                    { required: true, message: 'Please enter position name' },
                    { min: 2, message: 'Position name must be at least 2 characters' }
                  ]}
                >
                  <Input 
                    placeholder="Enter position name" 
                    onChange={handlePositionNameChange}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="position_code"
                  label="Position Code (Auto-generated)"
                  rules={[
                    { required: true, message: 'Please enter position code' },
                    { min: 2, message: 'Position code must be at least 2 characters' }
                  ]}
                >
                  <Input
                    placeholder="Auto-generated from position name"
                    onChange={(e) => {
                      form.setFieldsValue({ position_code: e.target.value.toUpperCase() });
                    }}
                    suffix={
                      <Tooltip title="Auto-generated from position name. You can edit it if needed.">
                        <FileTextOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Please select a department' }]}
            >
              <Select placeholder="Select Department">
                {departments.map(dept => (
                  <Option key={dept.value || dept.id} value={(dept.value || dept.id)?.toString()}>
                    {dept.label || dept.department_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={4}
                placeholder="Enter position description"
              />
            </Form.Item>
            <Form.Item
              name="status"
              label="Status"
            >
              <Select>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Layout>
  );
};

export default EmployeePositions;
